import React, { useRef, useState, useMemo, useEffect } from 'react';
import { FormHandles } from '@unform/core';

import { useHistory, useLocation } from 'react-router-dom';
import Filter from './Filter';
import Table from './Table';
import DashboardTemplate from '../../../../templates/Dashboard';
import PageTree from '../../../../components/PageTree';
import { ElementPageTree } from '../../../../components/PageTree/types';
import Spinner from '../../../../components/Spinner';
import Pagination from '../../../../components/Pagination';
import {
  getAttendancesByFilter,
  GetAttendancesByFilter as ListFilter,
  GetAttendancesByFilterResponse as ListData,
} from '../../../../services/reports/attendances';
import { useCurrentEffect } from '../../../../lib/use-current-effect';
import MainAreasEnum from '../../../../enums/MainAreasEnum';
import AppointmentTypeEnum from '../../../../enums/AppointmentTypeEnum';

const DEFAULT_LIST_FILTER: ListFilter = {
  orderBy: 'patientName',
  ordenationType: 'ASC',
  limit: 10,
};

function isValidDate(date: Date) {
  return !Number.isNaN(date.getTime());
}

function parsePageFromSearch(search: string) {
  const params = new URLSearchParams(search);
  const pageFromUrl = Number(params.get('page') || 1);

  return Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
}

function parseListFilterFromSearch(search: string): Partial<ListFilter> & {
  icd10?: string;
} {
  const params = new URLSearchParams(search);

  const parsed: Partial<ListFilter> & { icd10?: string } = {};

  const patientName = params.get('patientName') || '';
  if (patientName) parsed.patientName = patientName;

  const professionalName = params.get('professionalName') || '';
  if (professionalName) parsed.professionalName = professionalName;

  const sheet = params.get('sheet') || '';
  if (sheet) parsed.sheet = sheet;

  const riskDegree = params.get('riskDegree') || '';
  if (riskDegree) parsed.riskDegree = riskDegree;

  const attendanceType = params.get(
    'attendanceType',
  ) as AppointmentTypeEnum | null;
  if (
    attendanceType === AppointmentTypeEnum.VIDEO ||
    attendanceType === AppointmentTypeEnum.PHONE ||
    attendanceType === AppointmentTypeEnum.SMS ||
    attendanceType === AppointmentTypeEnum.PRESENTIAL
  ) {
    parsed.attendanceType = attendanceType;
  }

  const orderBy = params.get('orderBy') || '';
  if (orderBy) parsed.orderBy = orderBy;

  const ordenationType = params.get('ordenationType') as
    | ListFilter['ordenationType']
    | null;
  if (ordenationType === 'ASC' || ordenationType === 'DESC') {
    parsed.ordenationType = ordenationType;
  }

  const limitFromUrl = Number(params.get('limit') || '');
  if (Number.isFinite(limitFromUrl) && limitFromUrl > 0) {
    parsed.limit = limitFromUrl;
  }

  const icd10 = params.get('icd10') || '';
  if (icd10) parsed.icd10 = icd10;

  const startDate = params.get('startDate') || '';
  if (startDate) {
    const d = new Date(startDate);
    if (isValidDate(d)) parsed.startDate = d;
  }

  const endDate = params.get('endDate') || '';
  if (endDate) {
    const d = new Date(endDate);
    if (isValidDate(d)) parsed.endDate = d;
  }

  return parsed;
}

function setOrDeleteParam(
  params: URLSearchParams,
  key: string,
  value: string | number | undefined | null,
) {
  if (value === undefined || value === null || String(value).trim() === '') {
    params.delete(key);
    return;
  }

  params.set(key, String(value));
}

const List: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();

  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(() =>
    parsePageFromSearch(location.search),
  );

  const [listFilter, setListFilter] = useState<ListFilter>(() => ({
    ...DEFAULT_LIST_FILTER,
    ...parseListFilterFromSearch(location.search),
  }));
  const [listData, setListData] = useState<ListData>({
    attendances: [],
    totalAttendances: 0,
    totalPages: 0,
    totalAttendancesOnPage: 0,
  });

  useEffect(() => {
    // Mantém o estado sincronizado com a URL (back/forward e links compartilhados)
    const pageFromUrl = parsePageFromSearch(location.search);
    const filterFromUrl = parseListFilterFromSearch(location.search);

    setCurrentPage(pageFromUrl);
    setListFilter((prev) => ({
      ...DEFAULT_LIST_FILTER,
      ...prev,
      ...filterFromUrl,
    }));
  }, [location.search]);

  const handleChangePage = (page: number) => {
    const params = new URLSearchParams(history.location.search);
    params.set('page', String(page));

    setCurrentPage(page);

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  };

  useEffect(() => {
    // Persiste filtros na URL para permitir "voltar" com estado intacto
    const params = new URLSearchParams(history.location.search);

    setOrDeleteParam(params, 'page', currentPage);
    setOrDeleteParam(params, 'patientName', listFilter.patientName);
    setOrDeleteParam(params, 'professionalName', listFilter.professionalName);
    setOrDeleteParam(params, 'attendanceType', listFilter.attendanceType);
    setOrDeleteParam(params, 'sheet', listFilter.sheet);
    setOrDeleteParam(params, 'riskDegree', listFilter.riskDegree);

    // @ts-expect-error: esse campo existe no formulário, mas não no tipo do service
    setOrDeleteParam(params, 'icd10', listFilter.icd10);

    // Mantém URL mais limpa (omite defaults)
    setOrDeleteParam(
      params,
      'orderBy',
      listFilter.orderBy && listFilter.orderBy !== DEFAULT_LIST_FILTER.orderBy
        ? listFilter.orderBy
        : '',
    );
    setOrDeleteParam(
      params,
      'ordenationType',
      listFilter.ordenationType &&
        listFilter.ordenationType !== DEFAULT_LIST_FILTER.ordenationType
        ? listFilter.ordenationType
        : '',
    );
    setOrDeleteParam(
      params,
      'limit',
      listFilter.limit && listFilter.limit !== DEFAULT_LIST_FILTER.limit
        ? listFilter.limit
        : '',
    );

    setOrDeleteParam(
      params,
      'startDate',
      listFilter.startDate && isValidDate(listFilter.startDate)
        ? listFilter.startDate.toISOString()
        : '',
    );
    setOrDeleteParam(
      params,
      'endDate',
      listFilter.endDate && isValidDate(listFilter.endDate)
        ? listFilter.endDate.toISOString()
        : '',
    );

    const nextSearch = params.toString();
    const currentSearch = history.location.search.replace(/^\?/, '');

    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: history.location.pathname,
        search: nextSearch,
      });
    }
  }, [currentPage, history, listFilter]);

  const pages = useMemo<ElementPageTree[]>(
    () => [{ name: 'Relatórios' }, { name: 'Atendimentos' }],
    [],
  );

  useCurrentEffect(
    (isCurrent) => {
      getAttendancesByFilter({ ...listFilter, page: currentPage })
        .then((listDataResponse) => {
          if (isCurrent()) {
            setListData(listDataResponse);
          }
        })
        .finally(() => {
          if (isCurrent()) {
            setIsLoading(false);
          }
        });
    },
    [currentPage, listFilter],
  );

  return (
    <DashboardTemplate name={MainAreasEnum.REPORTS}>
      <PageTree pages={pages} />
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Filter
            onChangePage={handleChangePage}
            setListFilter={setListFilter}
            formRef={formRef}
          />
          <Table setListFilter={setListFilter} listData={listData} />
          {listData.attendances.length > 0 && (
            <Pagination
              totalPages={listData.totalPages}
              page={currentPage}
              onChangePage={handleChangePage}
            />
          )}
        </>
      )}
    </DashboardTemplate>
  );
};

export default List;
